/**
 * Brand index — client-side narrowing over the complete vendor list.
 *
 * The list is rendered whole by Liquid. `shop.vendors` has no equivalent of the
 * 100-value ceiling Search & Discovery puts on `filter.p.vendor`, which is the
 * only reason this section exists: the storefront filter physically cannot show
 * every brand this catalogue carries. So narrowing here is a pure DOM pass over
 * ~180 nodes — no fetch, no Section Rendering API, nothing to debounce.
 *
 * Match text is baked into `data-brand` at render time. It carries the raw
 * vendor string as well as the display label, so a customer who types the name
 * exactly as MusicShop360 records it still lands on the brand even when the
 * section renames it for display.
 */
class GwBrandIndex extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-brand-search]');
    if (!this.input) return;

    this.clearButton = this.querySelector('[data-brand-clear]');
    this.status = this.querySelector('[data-brand-status]');
    this.empty = this.querySelector('[data-brand-empty]');
    this.rail = this.querySelector('[data-brand-rail]');
    this.entries = Array.from(this.querySelectorAll('[data-brand]'));
    this.groups = Array.from(this.querySelectorAll('[data-brand-group]'));

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.reset = this.reset.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.clearButton?.addEventListener('click', this.reset);

    // A value can outlive the page: browsers restore form state on a back
    // navigation and on a bfcache resume, so the list must be re-narrowed to
    // match what is already sitting in the box.
    this.apply();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('keydown', this.onKeydown);
    this.clearButton?.removeEventListener('click', this.reset);
  }

  get query() {
    return this.input.value.trim().toLowerCase();
  }

  onInput() {
    this.apply();
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || !this.query) return;

    // Stop Escape reaching the header drawer / overlay elements above us.
    event.preventDefault();
    event.stopPropagation();
    this.reset();
  }

  reset() {
    this.input.value = '';
    this.apply();
    this.input.focus();
  }

  apply() {
    const query = this.query;
    let matches = 0;

    for (const entry of this.entries) {
      const hit = !query || entry.dataset.brand.includes(query);
      entry.hidden = !hit;
      if (hit) matches += 1;
    }

    // A letter heading with nothing left under it is noise.
    for (const group of this.groups) {
      group.hidden = !group.querySelector('[data-brand]:not([hidden])');
    }

    // Letters stop meaning anything once the list is a filtered subset, and the
    // anchors they point at may now be hidden.
    if (this.rail) this.rail.hidden = Boolean(query);
    if (this.empty) this.empty.hidden = matches > 0;
    if (this.clearButton) this.clearButton.hidden = !query;
    this.toggleAttribute('searching', Boolean(query));

    this.announce(matches, query);
  }

  announce(matches, query) {
    if (!this.status) return;

    const template = query ? this.status.dataset.filtered : this.status.dataset.all;
    if (!template) return;

    this.status.textContent = template.replace('[count]', matches);
  }
}

if (!customElements.get('gw-brand-index')) {
  customElements.define('gw-brand-index', GwBrandIndex);
}
